import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, X, ArrowRight } from 'lucide-react';
import {
  VIDEO_TESTIMONIALS,
  PLACEHOLDER_SLOTS,
  extractYouTubeId,
  getYouTubeThumbnail,
} from '../data/video-testimonials';

/* ════════════════════════════════════════════════════════════════
   TestimonialWall — Cinematic Video Grid
   • Renders video testimonials in a mosaic (landscape spans 2 cols,
     vertical spans 1 col but taller).
   • Click a card → opens a dark lightbox modal w/ autoplay.
   • Empty slots become "Your Story Here" invitation cards.
   • Pulls data from /src/data/video-testimonials.js
   ════════════════════════════════════════════════════════════════ */

function VideoCard({ video, index, onPlay }) {
  const isYouTube = video.type === 'youtube';
  const ytId = isYouTube ? extractYouTubeId(video.src) : null;
  const poster =
    video.poster ||
    (isYouTube ? getYouTubeThumbnail(ytId) : '');

  return (
    <button
      type="button"
      className={`vtw-card vtw-card--${video.orientation || 'landscape'}`}
      data-testid={`vtw-video-card-${index}`}
      style={{ '--vtw-delay': `${index * 0.08}s` }}
      onClick={() => onPlay(video)}
      aria-label={`Play testimonial from ${video.name}`}
    >
      {poster ? (
        <img
          src={poster}
          alt={`${video.name} — video testimonial poster`}
          className="vtw-thumb"
          loading="lazy"
          onError={(e) => {
            // Fallback to default YouTube thumb if maxres not available
            if (isYouTube && !e.target.dataset.fallback) {
              e.target.dataset.fallback = '1';
              e.target.src = `https://i.ytimg.com/vi/${ytId}/0.jpg`;
            }
          }}
        />
      ) : (
        <div className="vtw-thumb vtw-thumb--placeholder" />
      )}

      <div className="vtw-overlay" />

      <div className="vtw-play-ring" aria-hidden="true">
        <Play size={26} strokeWidth={2} fill="currentColor" />
      </div>

      <div className="vtw-info">
        {video.quote && (
          <p className="vtw-quote">&ldquo;{video.quote}&rdquo;</p>
        )}
        <div className="vtw-meta">
          <span className="vtw-name">{video.name}</span>
          {video.subtitle && (
            <span className="vtw-subtitle">{video.subtitle}</span>
          )}
          {video.detail && (
            <span className="vtw-detail">{video.detail}</span>
          )}
        </div>
      </div>
    </button>
  );
}

function InviteCard({ index, showCTA }) {
  return (
    <article
      className="vtw-card vtw-card--invite"
      data-testid={`vtw-invite-card-${index}`}
      style={{ '--vtw-delay': `${index * 0.08}s` }}
    >
      <p className="vtw-invite-eyebrow">Your Story</p>
      <p className="vtw-invite-headline">
        This space is waiting for you.
      </p>
      <p className="vtw-invite-sub">
        Share what The Village has meant to you — yours might be the voice
        another woman needs to hear.
      </p>
      {showCTA && (
        <Link
          to="/book-cabrini"
          className="vtw-invite-btn"
          data-testid={`vtw-share-story-btn-${index}`}
        >
          Share Your Story <ArrowRight size={14} strokeWidth={2} />
        </Link>
      )}
    </article>
  );
}

function Lightbox({ video, onClose }) {
  const isYouTube = video.type === 'youtube';
  const ytId = isYouTube ? extractYouTubeId(video.src) : null;

  // Close on ESC + lock body scroll while open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="vtw-lightbox"
      data-testid="vtw-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Testimonial video — ${video.name}`}
      onClick={onClose}
    >
      <div
        className={`vtw-lightbox-inner vtw-lightbox-inner--${video.orientation || 'landscape'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="vtw-lightbox-close"
          onClick={onClose}
          aria-label="Close video"
          data-testid="vtw-lightbox-close"
        >
          <X size={20} strokeWidth={2.2} />
        </button>

        <div className="vtw-lightbox-frame">
          {isYouTube ? (
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={`${video.name} testimonial`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <video src={video.src} controls autoPlay playsInline />
          )}
        </div>

        <div className="vtw-lightbox-caption">
          <span className="vtw-lightbox-name">{video.name}</span>
          {video.subtitle && (
            <span className="vtw-lightbox-subtitle"> — {video.subtitle}</span>
          )}
          {video.detail && (
            <span className="vtw-lightbox-detail">{video.detail}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TestimonialWall({
  title = 'Voices from The Village',
  showCTA = true,
}) {
  const [activeVideo, setActiveVideo] = useState(null);
  const handlePlay = useCallback((v) => setActiveVideo(v), []);
  const handleClose = useCallback(() => setActiveVideo(null), []);

  const slots = Math.max(
    0,
    PLACEHOLDER_SLOTS - 0 // keep invite count independent of how many videos exist
  );

  return (
    <section className="tw" data-testid="testimonial-wall">
      <div className="tw-header">
        <p className="tw-eyebrow">Voices of the Village Manifestors</p>
        <h2 className="tw-title">{title}</h2>
        <p className="tw-sub">
          Real women. Real voices. Real transformation. Click any portrait to
          hear their story — yours could be next.
        </p>
      </div>

      <div className="vtw-grid" data-testid="vtw-grid">
        {VIDEO_TESTIMONIALS.map((v, i) => (
          <VideoCard key={`v-${i}`} video={v} index={i} onPlay={handlePlay} />
        ))}

        {Array.from({ length: slots }).map((_, i) => (
          <InviteCard
            key={`inv-${i}`}
            index={VIDEO_TESTIMONIALS.length + i}
            showCTA={showCTA}
          />
        ))}
      </div>

      {activeVideo && (
        <Lightbox video={activeVideo} onClose={handleClose} />
      )}
    </section>
  );
}
